import { useParams, Navigate } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { getIndustry, INDUSTRIES } from "@/data/industries";
import { REGIONS, CITIES } from "@/data/locations";
import {
  BRAND_URL, Breadcrumbs, LocalHero, TextBand, BulletList, LinkGrid, RelatedWork, FaqList, CtaBand, breadcrumbLd, faqLd,
} from "@/components/LocalSections";

const areaLinks = [
  ...REGIONS.map((r) => ({ to: `/web-design/${r.slug}`, label: r.name })),
  ...CITIES.slice(0, 6).map((c) => ({ to: `/web-design/${c.slug}`, label: c.name })),
  ...CITIES.filter((c) => c.region === "orange-county").slice(0, 4).map((c) => ({ to: `/web-design/${c.slug}`, label: c.name })),
];

export default function IndustryPage() {
  const { slug } = useParams();
  const ind = getIndustry(slug);
  if (!ind) return <Navigate to="/industries" replace />;
  const path = `/industries/${ind.slug}`;
  const crumbs = [{ name: "Home", to: "/" }, { name: "Industries", to: "/industries" }, { name: ind.name, to: path }];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbLd(crumbs),
      {
        "@type": "Service",
        name: `Website Design for ${ind.name}`,
        provider: { "@type": "ProfessionalService", name: "Redkey Web Design", url: BRAND_URL },
        areaServed: [{ "@type": "AdministrativeArea", name: "Inland Empire, CA" }, { "@type": "AdministrativeArea", name: "Orange County, CA" }],
        description: ind.metaDescription,
        url: `${BRAND_URL}${path}`,
      },
      faqLd(ind.faq),
    ],
  };
  const prefill = { industry: ind.short };
  return (
    <div data-testid={`industry-page-${ind.slug}`}>
      <PageHead title={ind.metaTitle} description={ind.metaDescription} canonical={`${BRAND_URL}${path}`} jsonLd={jsonLd} />
      <Breadcrumbs items={crumbs} />
      <LocalHero eyebrow="§ Industry" title={`Website Design for ${ind.name}`} line={ind.headline} intro={ind.intro} prefill={prefill} testId="industry" />
      <TextBand eyebrow="§ The problem" title="What's holding most sites back" alt>
        <BulletList items={ind.problems} />
      </TextBand>
      <TextBand eyebrow="§ What you get" title={`Built for ${ind.short.toLowerCase()}`}>
        <BulletList items={ind.features} />
      </TextBand>
      <RelatedWork ids={ind.relatedWorkIds} testId="industry-work" />
      <LinkGrid eyebrow="§ Local" title="Serving the Inland Empire & Orange County" testId="industry-areas" dark links={areaLinks} />
      <FaqList faq={ind.faq} testId="industry-faq" />
      <LinkGrid eyebrow="§ Other industries" title="More industries we serve" testId="industry-others"
        links={INDUSTRIES.filter((i) => i.slug !== ind.slug).map((i) => ({ to: `/industries/${i.slug}`, label: i.short }))} />
      <CtaBand text={`Ready for a ${ind.short.toLowerCase()} website that brings in better leads?`} prefill={prefill} testId="industry" />
    </div>
  );
}
